import { useEffect, useMemo, useState } from 'react';
import { match } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { withLoading } from 'utils';
import { changeLocale } from 'actions/statusActions';

import classes from './Register.module.scss';
import RegisterForm from './RegisterForm/RegisterForm';
import RegisterHeader from './RegisterHeader';
import api from 'api';

const langToFont: Record<string, string> = {
  din: 'din',
};

type RegisterProps = {
  history: any;
  location?: any;
  match: match<{ code: string; pendingRegistration?: string }>;
  internationalCustomers?: boolean;
};

const Register = ({ match, history, location, internationalCustomers = false }: RegisterProps) => {
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state: any) => state.status.lang);
  const translate = useSelector((state: any) => state.status.translate);
  const [checkingCode, setCheckingCode] = useState<boolean>(true);
  const [locations, setLocations] = useState<Partial<string[]>>();
  const [onePersonProduct, setOnePersonProduct] = useState<boolean>(false);
  const [noPlansDialog, setNoPlansDialog] = useState<{ open: boolean; buyUrl: string }>({ open: false, buyUrl: '' });

  const cleanRegistrationForm = true;

  const forcedRegistrationLocale = useMemo(() => {
    if (!internationalCustomers) return undefined;

    const params = new URLSearchParams(location?.search || window.location.search || '');
    const locale = String(params.get('locale') || '').trim();

    return locale || undefined;
  }, [internationalCustomers, location?.search]);

  useEffect(() => {
    if (internationalCustomers && forcedRegistrationLocale) {
      dispatch(changeLocale(forcedRegistrationLocale) as any);
    }
  }, [dispatch, forcedRegistrationLocale, internationalCustomers]);

  useEffect(() => {
    const validateCode = async (code: string) => {
      const result = await api.organizationPurchase.checkCodeAvailability(code);

      if (result.success) {
        setCheckingCode(false);
        setLocations(result.locations);
        if (result.onePerson?.onePerson) {
          console.log('Register result.onePerson: ', result);
          setOnePersonProduct(result.onePerson.onePerson);
        }
      } else if (result?.errorCode === 'NO_PLANS') {
        const buyUrl =
          (typeof result?.buyUrl === 'string' && result.buyUrl.trim())
            ? result.buyUrl.trim()
            : 'https://lp.familylearningcompany.com/website/checkout';

        console.log('[Register][NO_PLANS]', { code, buyUrl });
        setCheckingCode(false);
        setNoPlansDialog({ open: true, buyUrl });
      } else {
        alert(result?.error || 'Something goes wrong');
        history.goBack();
      }
    };

    validateCode(match.params.code || match.params.pendingRegistration || '');
  }, [history, match.params.code, match.params.pendingRegistration]);

  const fontFamily = `var(--font-${
    langToFont[currentLanguage?.code] || 'base'
  })`;

  return (
    <div style={{ fontFamily }} className={classes.wrapper}>
      <RegisterHeader hideLanguages={cleanRegistrationForm} />

      <main className={classes.main}>
        {checkingCode ? (
          <p>Checking you code...</p>
        ) : noPlansDialog.open ? (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
            <div
              role="dialog"
              aria-modal="true"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                color: '#111',
                borderRadius: 12,
                padding: 18,
                width: 'min(560px, calc(100vw - 32px))',
                boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ fontSize: 16, lineHeight: 1.4 }}>
                {translate(
                  3668,
                  'There are no family plans left. You can contact someone in your organization and ask them to order more, or click the BUY button to purchase a product yourself.',
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, gap: 10 }}>
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(17,17,17,0.15)',
                    borderRadius: 9999,
                    padding: '10px 14px',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const url = (noPlansDialog.buyUrl || '').toString().trim() || 'https://lp.familylearningcompany.com/website/checkout';
                    console.log('[Register][NO_PLANS][buy]', { url });
                    window.location.href = url;
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 0,
                    borderRadius: 9999,
                    padding: '10px 16px',
                    cursor: 'pointer',
                    background: '#111827',
                    color: '#fff',
                    fontWeight: 700,
                  }}
                >
                  Buy
                </button>
              </div>
            </div>
          </div>
        ) : (
          <RegisterForm
            initialValues={{
              messageLocale: forcedRegistrationLocale || 'en',
              onePersonProduct,
            }}
            internationalRegistration={internationalCustomers}
            compactRegistration={cleanRegistrationForm}
            forcedMessageLocale={forcedRegistrationLocale}
            submit={(values) => withLoading(dispatch, async () => {
              return api.organizationPurchase.registerByCode(match.params.code || match.params.pendingRegistration || '', values);
            })}
            locations={locations}
          />
        )}
      </main>
    </div>
  );
};

export default Register;
